// src/Pages/Dashboard/widgets/ProjectView/Tabs/Materials/Component/MaterialReportModal.jsx

import React, { useEffect } from "react";
import { FaTimes, FaFileCsv, FaFilePdf, FaCalendarAlt } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../../../../../../Stylesheets/Dashboard/Tabs/Expenses/ViewReportModal.css";

const toISODate = (d) => {
  if (!d || Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const fromISODate = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const MaterialReportModal = ({
  show,
  onClose,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  category,
  setCategory,
  format,
  setFormat,
  handleExportCSV,
  handleExportPDF,
  mode = "export",       // "export" | "filters"
  onApply,
  applyLabel = "Apply Filters",
}) => {
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [show, onClose]);

  // 🔹 simple derived values (no hooks)
  const startObj = fromISODate(startDate);
  const endObj = fromISODate(endDate);

  const applyPreset = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(to.getDate() - days);
    setStartDate(toISODate(from));
    setEndDate(toISODate(to));
  };

  const applyThisMonth = () => {
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setStartDate(toISODate(from));
    setEndDate(toISODate(to));
  };

  const handlePrimary = () => {
    if (mode === "filters" && onApply) {
      onApply();
    } else if (handleExportPDF) {
      handleExportPDF();
    }
  };

  if (!show) return null;

  return (
    <div
      className="report-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="report-modal report-modal-anim">
        {/* HEADER */}
        <div className="report-modal-header">
          <div>
            <h2 className="report-modal-title">Export Material Report</h2>
            <p className="report-modal-subtitle">
              Pick a range and export material-wise report.
            </p>
          </div>
          <button
            className="report-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* BODY */}
        <div className="report-modal-body">
          {/* DATE RANGE */}
          <div className="report-section">
            <label className="report-label">Date Range</label>

            <div className="date-presets">
              <button type="button" onClick={() => applyPreset(7)}>
                Last 7 days
              </button>
              <button type="button" onClick={() => applyPreset(30)}>
                Last 30 days
              </button>
              <button type="button" onClick={applyThisMonth}>
                This month
              </button>
            </div>

            <div className="date-range-grid">
              <div className="date-picker-wrapper">
                <FaCalendarAlt className="date-icon" />
                <DatePicker
                  selected={startObj}
                  onChange={(d) => setStartDate(toISODate(d))}
                  placeholderText="Start date"
                  dateFormat="yyyy-MM-dd"
                  className="report-input"
                  maxDate={endObj || new Date()}
                />
              </div>

              <div className="date-picker-wrapper">
                <FaCalendarAlt className="date-icon" />
                <DatePicker
                  selected={endObj}
                  onChange={(d) => setEndDate(toISODate(d))}
                  placeholderText="End date"
                  dateFormat="yyyy-MM-dd"
                  className="report-input"
                  minDate={startObj}
                  maxDate={new Date()}
                />
              </div>
            </div>
          </div>

          {/* MATERIAL FILTER */}
          <div className="report-section">
            <label className="report-label">Material Filter (optional)</label>
            <select
              className="report-select"
              value={category || "all"}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="all">All materials</option>
              {/* later add groups/tags here */}
            </select>
          </div>

          {/* FORMAT */}
          <div className="report-section">
            <label className="report-label">Format</label>
            <div className="format-toggle-advanced">
              <button
                type="button"
                className={`format-pill ${
                  format === "summary" ? "active" : ""
                }`}
                onClick={() => setFormat("summary")}
              >
                Summary
              </button>
              <button
                type="button"
                className={`format-pill ${
                  format === "detailed" ? "active" : ""
                }`}
                onClick={() => setFormat("detailed")}
              >
                Detailed
              </button>
            </div>
            <div className="format-hint">
              Summary = totals only. Detailed = full material-level entries
              (once implemented).
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="report-modal-footer">
          <button className="btn-ghost" onClick={onClose}>
            {mode === "filters" ? "Cancel" : "Close"}
          </button>

          {mode === "filters" ? (
            <button className="btn-primary" onClick={handlePrimary}>
              {applyLabel}
            </button>
          ) : (
            <div className="export-buttons">
              <button className="btn-outline" onClick={handleExportCSV}>
                <FaFileCsv /> Download CSV
              </button>
              <button className="btn-primary" onClick={handlePrimary}>
                <FaFilePdf /> Open Preview
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaterialReportModal;
